import {useState} from 'react';
import {useNavigate} from 'react-router';
import {
    deleteDisclosure,
    deleteReport,
    resetReportToDraft,
    updateReportDoc
} from '../../../../firebase/firestore/reports';
import {triggerExpertProfileUpdateForAuthors} from '../../../experts/expertProfileService';
import {exportReportToDocx} from '../../../../utils/docxExportService';
import {getReportById} from '../../../../firebase/firestore/reports';
import {useReportStore} from '../../useReportStore';
import {readSerializableHeaderFromYDoc} from '../../../../utils/documentHeaderMeta';
import {
    buildExportMetaAsync,
    downloadDocxBlob,
    loadSnapshotData,
} from '../../../../utils/exportReportHelper';
import {resolveExportSnapshot} from '../../../../utils/resolveExportSnapshot';
import useYDocStore from '../../../../stores/useYDocStore';

/**
 * Custom hook for report actions (delete, export, submit for review)
 *
 * @param {string} reportId - The report ID
 * @param {string} title - The report title
 * @param {string} status - Current report status
 * @param {object} metaFromStore - Report metadata from store
 * @param {object} user - Current user object
 * @param {function} updateReport - Function to update report in store
 * @param {object} productType - Product type object with value and label
 * @returns {object} Action handlers and loading states
 */
export function useReportActions({reportId, title, status, metaFromStore, user, updateReport, productType}) {
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState(false);
    const [submittingReview, setSubmittingReview] = useState(false);
    const [recalling, setRecalling] = useState(false);
    const editorView = useReportStore(state => state.editorView);

    const handleDelete = async () => {
        if (!reportId) return;
        try {
            setDeleting(true);

            const isDisclosure = productType?.value === 'disclosure' ||
                productType?.value === 'ndp1_disclosure';

            if (isDisclosure) {
                await deleteDisclosure(reportId);
            } else {
                await deleteReport(reportId);
            }

            navigate('/products');
        } catch (e) {
            console.error('Failed to delete report', e);
            throw e;
        } finally {
            setDeleting(false);
        }
    };

    const handleExport = async () => {
        try {
            const snapshot = await resolveExportSnapshot(reportId, editorView);

            if (snapshot?.content) {
                const ydoc = useYDocStore.getState().ydoc;
                const headerData = snapshot.metadata
                    || (ydoc ? readSerializableHeaderFromYDoc(ydoc) : null);
                const meta = await buildExportMetaAsync({
                    baseMeta: metaFromStore,
                    snapshotMeta: snapshot.metadata,
                    headerData,
                    content: snapshot.content,
                    title,
                    reportId,
                });
                const blob = await exportReportToDocx(
                    snapshot.content,
                    meta,
                    snapshot.references || [],
                    title,
                );
                downloadDocxBlob(blob, title, reportId);
                return;
            }

            // Legacy fallback: snapshot path via object storage.
            const reportData = await getReportById(reportId);
            const snapshotPath = reportData?.snapshotPath;

            if (snapshotPath) {
                const legacySnapshot = await loadSnapshotData(reportId, snapshotPath);
                if (legacySnapshot) {
                    const ydoc = useYDocStore.getState().ydoc;
                    const headerData = legacySnapshot.metadata
                        || (ydoc ? readSerializableHeaderFromYDoc(ydoc) : null);
                    const meta = await buildExportMetaAsync({
                        baseMeta: metaFromStore,
                        snapshotMeta: legacySnapshot.metadata,
                        headerData,
                        content: legacySnapshot.content,
                        title,
                        reportId,
                    });
                    const blob = await exportReportToDocx(
                        legacySnapshot.content,
                        meta,
                        legacySnapshot.references,
                        title,
                    );
                    downloadDocxBlob(blob, title, reportId);
                    return;
                }
            }

            throw new Error('No content available for export — please open the report in the editor and try again');
        } catch (err) {
            console.error('Error exporting to MS Word:', err);
            throw err;
        }
    };

    const handleSubmitForReview = async () => {
        const next = 'Awaiting Review';
        if (status === next) return;

        try {
            setSubmittingReview(true);
            updateReport({status: next});
            if (reportId) {
                await updateReportDoc(reportId, {status: next});
            }
        } catch (err) {
            console.error('Failed to submit for review', err);
            throw err;
        } finally {
            setSubmittingReview(false);
        }
    };

    const handleRecall = async () => {
        try {
            setRecalling(true);
            const authorsForProfile =
                status === 'approved' && Array.isArray(metaFromStore?.authors) ? metaFromStore.authors : [];
            updateReport({status: 'Draft', validation: null});
            if (reportId) {
                await resetReportToDraft(reportId);
                if (authorsForProfile.length > 0) {
                    triggerExpertProfileUpdateForAuthors(authorsForProfile).catch((err) =>
                        console.error('[useReportActions] Expert profile update after recall failed:', err)
                    );
                }
            }
        } catch (err) {
            console.error('Failed to recall report', err);
            throw err;
        } finally {
            setRecalling(false);
        }
    };

    return {
        handleDelete,
        handleExport,
        handleSubmitForReview,
        handleRecall,
        deleting,
        submittingReview,
        recalling
    };
}
